<template>
	<div>
		<v-snackbar v-model="goalDeletedNotif" top>
			Goal Deleted Successfully
			<v-btn color="pink" flat @click="goalDeletedNotif = false">Close</v-btn>
		</v-snackbar>

		<v-snackbar v-model="goalSavedNotif" top>
			Goal Updated
			<v-btn color="pink" flat @click="goalSavedNotif = false">Close</v-btn>
		</v-snackbar>

		<loading-modal v-if="isLoading" loading-text="Loading Report Data..." />

		<div v-if="reportData !== null">


			<div class="report-toolbar">
				<v-layout row>
					<v-flex>
						<v-layout row wrap>
								<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">	
									<v-text-field v-debounce:300ms="updateSearch" ref="focusAfterTagSelector" append-icon="search" label="Search Employees" autofocus="" background-color="#fff"></v-text-field>
								</v-flex>
								<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
									<tag-selector :options="tagList"
												:selectedOptions="selectedTags"
												:isMatchAll="matchAllTags"
												placeholder="Enter Tag"
												@on-selected-tags-change="updateSelectedTags"
												@on-match-all-tags-change="updateMatchAllTags"
												@on-leave="onLeaveTagSelector"></tag-selector>
								</v-flex>
								<v-flex xl3 lg4 md4 sm6 xs12>
									<v-select v-model="filterCategory" :items="filterCategories" label="Goal Type" item-text="text" item-value="value" required></v-select>
								</v-flex>
						</v-layout>
						<v-layout row wrap>
							<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
								<v-select v-model="filterDepartment" :items="departmentOptions" item-text="name" item-value="id" label="Department"></v-select>
							</v-flex>
							<v-flex grow>
								<v-switch v-model="includeComplete">
									<div slot="label">
										<div v-show="includeComplete == true">
											Include goals completed
											<a style="padding-top: 10px; padding-bottom: 10px;" href="#" @click.stop="showGoalsCompletedWithinModal = true">{{ goalsCompletedWithinFilter.text }}</a>
										</div>
										<div v-show="includeComplete == false">Include completed goals</div>
									</div>
								</v-switch>
							</v-flex>
						</v-layout>
					</v-flex>

					<v-flex shrink>
						<v-tooltip bottom>
							<template v-slot:activator="{ on }">
								<v-btn icon v-on="on" @click="print">
									<v-icon>print</v-icon>
								</v-btn>
							</template>
							<span>Print Report</span>
						</v-tooltip>
					</v-flex>
				</v-layout>				
			</div>

			<div v-if="filteredEmployees == null || filteredEmployees.length === 0" class="text-xs-left mt-3">
                <v-alert :value="true" color="info" outline icon="info"> No report data to display </v-alert>
            </div>

			<div v-else>
				<v-spacer />

				<v-expansion-panel v-model="expansionPanel" expand>
					<v-expansion-panel-content v-for="(employee, i) in filteredEmployees" :key="i" style="background-color:#EEEEEE;">
						<template v-slot:header>
							<div>
								<v-avatar size="30px">
									<img v-if="employee !== null" :src="getAvatar(employee.id)" class="avatar-img" />
								</v-avatar>
								<a class="ml-3 employee-name">{{ employee.fullName }}</a>
							</div>
						</template>

						<v-data-table class="goals-table elevation-1" :items="employee.goals" hide-actions hide-headers>
							<template v-slot:items="props">
								<tr v-if="renderRow" :class="{ 'goal-complete': props.item.complete }" @click="viewGoal(props.item)">
									<td>{{ props.item.goal }}</td>
									<td class="text-xs-right" style="width: 200px;">
										<v-chip class="v-chip--x-small" :color="getCategoryChipColor(props.item.category)" text-color="white">{{ getCategoryChipText(props.item.category) }}</v-chip>
									</td>
									<td class="text-xs-left" style="width: 200px;">
										<div v-if="!props.item.complete" :class="{ overdue: isOverdue(props.item), upcoming: isComingUp(props.item) }">
											<v-icon>mdi-alarm</v-icon>
											<AppDate :key="props.item.goal.id" :date="props.item.dueDateUTC" null-date="N/A" prepend-tooltip="Due" show-date />
										</div>
										<div v-else>
											<v-chip v-if="props.item.complete" class="v-chip--x-small" color="green" small text-color="white">Complete</v-chip>
										</div>
									</td>
									<td class="text-xs-right" style="width: 1px;">
										<goal-progress :goal="props.item" />
									</td>
								</tr>
							</template>
						</v-data-table>
					</v-expansion-panel-content>
				</v-expansion-panel>

			</div>
		</div>

		<employee-goal-editor v-if="goalEditorDialog" 
			title="Edit Goal" 
			:dialog="goalEditorDialog" 
			:goal="editedGoal" 
			:employeeId="editedGoalEmployeeId"
			@onSaved="onGoalSaved" 
			@onDeleted="onGoalDeleted" 
			@closeEditor="goalEditorDialog = false">
		</employee-goal-editor>

		<selection-modal v-if="showGoalsCompletedWithinModal" title="Include goals completed" :items="goalsCompletedWithinOptions" :default-selected-val="goalsCompletedWithinFilter" :dialog="showGoalsCompletedWithinModal" @onSave="setGoalsCompletedWithinFilter" @onClose="showGoalsCompletedWithinModal = false" />
	</div>
</template>

<script>
	import moment from 'moment';
	import EmployeeGoalEditor from '@/components/employee/EmployeeGoalEditor';
	import GoalProgress from '@/components/goals/GoalProgress';
	import avatarMixin from '@/mixins/avatarMixin';
	import goalsMixin from '@/mixins/goalsMixin';
	import { mapGetters } from 'vuex';
	import LoadingModal from '@/components/LoadingModal';
	import SelectionModal from '@/components/helpers/SelectionModal';
	import TagSelector from '@/components/tags/TagSelector';

	export default {
		props: {
			goalId: {
				required: false,
				type: String,
				default: null
			},
			mineOnly: {
				required: false,
				default: false,
				type: Boolean
			}
		},
		data() {
			return {
				reportData: null,
				goalEditorDialog: false,
				editedGoal: undefined,
				editedGoalEmployeeId: null,
				goalSavedNotif: false,
				goalDeletedNotif: false,
				filterDepartment: -1,
				filterCategory: -1,
				renderRow: true,
				isLoading: false,
				includeComplete: true,
				goalsCompletedWithinFilter: { value: 1, text: 'within the last month' },
				goalsCompletedWithinOptions: [{ value: 0, text: 'within the last week' }, { value: 1, text: 'within the last month' }, { value: 2, text: 'within the last quarter' }, { value: 3, text: 'within the last year' }],
				showGoalsCompletedWithinModal: false,
				search: null,
				selectedTags: [],
				matchAllTags: false
			};
		},
		components: {
			GoalProgress,
			EmployeeGoalEditor,
			LoadingModal,
			SelectionModal,
			TagSelector
		},
		async created() {
			this.isLoading = true;
			await this.getReportData();
			this.isLoading = false;

			if (this.goalId !== null) {
				var foundGoal = this.findGoal(this.goalId);
				if (foundGoal !== null) {
					this.viewGoal(foundGoal);
				}
			}
		},
		watch: {
			filteredEmployees() {
				this.renderRow = false;

				this.$nextTick().then(() => {
					this.renderRow = true;
				});
			}
		},
		computed: {
			...mapGetters({
				departmentOptions: 'departments/departmentOptions',
				loggedInEmployee: 'loggedInEmployee',
				tagList: 'tags/employeeTagList'
			}),
			filteredEmployeeIds() {
				const filterOptions = {
					search: this.search,
					tags: this.selectedTags,
					matchAllTags: this.matchAllTags,
					departmentId: this.filterDepartment !== -1 ? this.filterDepartment : null
				};
				return this.$_getFilteredEmployeeIds(this.loggedInEmployee, filterOptions);
			},
			filteredEmployees() {
				if (this.reportData !== null) {
					let filtered = JSON.parse(JSON.stringify(this.reportData.employees));

					// Default filtering
					filtered = filtered.filter(x => this.filteredEmployeeIds.includes(x.id));

					if (this.mineOnly) {
						filtered = filtered.filter(x => x.id == this.loggedInEmployee.id);
					}

					// Filter Goals
					for (let i = 0; i < filtered.length; i++) {
						// Category
						if (this.filterCategory > -1) {
							filtered[i].goals = filtered[i].goals.filter(x => x.category === this.filterCategory);
						}
						// Include completed
						var _this = this;
						if (!this.includeComplete) {
							filtered[i].goals = filtered[i].goals.filter(function(x) {
								return x.complete === false;
							});
						}
						if (this.includeComplete) {
							filtered[i].goals = filtered[i].goals.filter(function(x) {
								switch (_this.goalsCompletedWithinFilter.value) {
									case 0:
										return x.complete === false || (x.complete === true && moment().diff(x.updatedDateUTC, 'weeks') <= 1);
									case 1:
										return x.complete === false || (x.complete === true && moment().diff(x.updatedDateUTC, 'months') <= 1);
									case 2:
										return x.complete === false || (x.complete === true && moment().diff(x.updatedDateUTC, 'months') <= 3);
									case 3:
										return x.complete === false || (x.complete === true && moment().diff(x.updatedDateUTC, 'months') <= 12);
								}
							});
						}
					}

					filtered = filtered.filter(x => x.goals.length > 0);

					return filtered;
				}

				return null;
			},
			expansionPanel() {
				return this.filteredEmployees.map(x => true);
			}
		},
		mixins: [avatarMixin, goalsMixin],
		methods: {
			setGoalsCompletedWithinFilter(val) {
				this.goalsCompletedWithinFilter = val;
				this.showGoalsCompletedWithinModal = false;
			},
			findGoal(id) {
				if (this.reportData !== null && this.reportData.employees !== null) {
					for (let i = 0; i < this.reportData.employees.length; i++) {
						if (this.reportData.employees[i].goals != null) {
							for (let j = 0; j < this.reportData.employees[i].goals.length; j++) {
								if (this.reportData.employees[i].goals[j].id === this.goalId) {
									return this.reportData.employees[i].goals[j];
								}
							}
						}
					}
				}
			},
			updateSearch(val) {
				this.search = val;
			},
			print() {
				window.print();
			},
			async getReportData() {
				try {
					const response = await this.$store.dispatch('reports/get', 'GoalStatusReport');
					if (response) {
						response.data.employees = response.data.employees.map(employee => {
							employee.goals = employee.goals.map(goal => {
								goal.employeeId = employee.id;
								return goal;
							});
							return employee;
						});

						this.reportData = response.data;
					}
				} catch (e) {
					console.log(e);
				}
			},
			isOverdue(goal) {
				return goal.dueDateUTC !== null && moment().isAfter(goal.dueDateUTC);
			},
			isComingUp(goal) {
				return goal.dueDateUTC !== null && moment().isBefore(goal.dueDateUTC) && moment().isAfter(moment(goal.dueDateUTC).subtract(1, 'week'));
			},
			viewGoal(goal) {
				this.$router.push({name: "employeeGoal", params: {
					id: goal.id,
					employeeId: goal.employeeId,
					goalPreloaded: goal
				}});
			},
			onGoalSaved(goal) {
				this.goalEditorDialog = false;
				this.goalSavedNotif = true;
				this.getReportData();
			},
			onGoalDeleted(id) {
				this.goalEditorDialog = false;
				this.goalDeletedNotif = true;
				this.getReportData();
			},
			updateSelectedTags(newSelectedTags) {
				this.selectedTags = newSelectedTags;
			},
			updateMatchAllTags(newMatchAll) {
				this.matchAllTags = newMatchAll;
			},
			onLeaveTagSelector() {
				this.$refs.focusAfterTagSelector.focus();
			}
		}
	};
</script>

<style scoped>
	.goal-complete {
		background-color: rgba(0, 255, 0, 0.1);
	}
	.employee-name {
		display: inline;
		color: info;
	}
	.report-toolbar {
		margin-bottom: 15px;
	}
	.goals-table tr:hover {
		cursor: pointer;
	}
	.goals-table td {
		height: 60px;
	}
	.overdue {
		color: #f44336;
	}
	.overdue .v-icon {
		color: #f44336;
	}
	.upcoming {
		color: #ff9800;
	}
	.upcoming .v-icon {
		color: #ff9800;
	}
</style>
